import React, { useContext } from "react";
import { ModelTsContext } from "../context/modelTS.context";
import { AppContext } from "../context/app.context";

export const GlobalLoadingIndicator = () => {
  const { appEnvirons } = useContext(AppContext);
  const {
    uniqueLocationsFetching,
    officeMetaDataFetching,
    userFetching,
    usersFetching,
    projectFetching,
    projectsFetching,
    candidatesFetching,
    candidatesAdditionalFetching,
    projectsAdditionalFetching,
    attributesFetching,
    logsFetching,
    shouldPollProjects,
    shouldPollAttributes,
    failedQueries,
    retryingQueries,
    tsUserDocument,
  } = useContext(ModelTsContext);

  const conversion = tsUserDocument?.conversion || "both";
  const isProduction = appEnvirons?.appEnv === "production";

  // Determine which queries affect the UI based on conversion setting:
  // - "realmOnly": TanStack doesn't affect UI at all → hide all indicators
  // - "both": Only these TanStack queries affect UI: user, uniqueLocations, officeMetaData, users, logs
  // - "tanstackOnly": All TanStack queries affect UI → show all indicators
  //
  // In Dev/UAT: Always show all indicators for debugging (regardless of conversion)
  // In Prod: Only show indicators for queries that actually affect the user's UI

  if (conversion === "realmOnly" && isProduction) {
    // realmOnly users in production: TanStack doesn't affect their UI at all
    return null;
  }

  // Queries that are ALWAYS TanStack-primary (regardless of conversion setting)
  const alwaysTanStackQueries =
    userFetching ||
    uniqueLocationsFetching ||
    officeMetaDataFetching ||
    usersFetching ||
    logsFetching;

  // Queries that are only TanStack-primary when conversion = "tanstackOnly"
  const tanstackOnlyQueries =
    projectFetching ||
    (projectsFetching && !shouldPollProjects) ||
    candidatesFetching ||
    candidatesAdditionalFetching ||
    projectsAdditionalFetching ||
    (attributesFetching && !shouldPollAttributes);

  // Filter failedQueries based on conversion setting (only in production)
  let relevantFailedQueries = failedQueries;
  if (isProduction && conversion === "both" && failedQueries && failedQueries.size > 0) {
    // For "both" users in production, only show failed queries that affect their UI
    const uiAffectingQueries = new Set(["user", "uniqueLocations", "officeMetaData", "users", "logs"]);
    relevantFailedQueries = new Set([...failedQueries].filter(q => uiAffectingQueries.has(q)));
  }

  // Determine what to show based on environment and conversion
  let isAnyFetching;
  if (!isProduction) {
    // Dev/UAT: Show ALL indicators for debugging
    isAnyFetching = alwaysTanStackQueries || tanstackOnlyQueries;
  } else if (conversion === "tanstackOnly") {
    // Production + tanstackOnly: Show ALL indicators (all queries affect UI)
    isAnyFetching = alwaysTanStackQueries || tanstackOnlyQueries;
  } else {
    // Production + both: Only show indicators for UI-affecting queries
    isAnyFetching = alwaysTanStackQueries;
  }

  // Check if any relevant queries have failed and need retry
  const hasFailedQueries = relevantFailedQueries && relevantFailedQueries.size > 0;
  const isRetrying = retryingQueries && retryingQueries.size > 0;

  // Show indicator if fetching OR if there are failed queries being retried
  if (!isAnyFetching && !hasFailedQueries) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        pointerEvents: "none", // Allow clicks to pass through
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
          padding: "24px 32px",
          backgroundColor: "rgba(0, 0, 0, 0.75)",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        {/* Spinner - orange when retrying failed queries, white when loading */}
        <div
          style={{
            width: "48px",
            height: "48px",
            border: hasFailedQueries && !isAnyFetching
              ? "4px solid rgba(255, 165, 0, 0.2)"
              : "4px solid rgba(255, 255, 255, 0.2)",
            borderTop: hasFailedQueries && !isAnyFetching
              ? "4px solid #FFA500"
              : "4px solid #ffffff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        
        {/* Loading text - shows different message based on state */}
        <div
          style={{
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.5px",
            textAlign: "center",
          }}
        >
          {hasFailedQueries && !isAnyFetching ? (
            <>
              <div style={{ color: "#FFA500" }}>
                {isRetrying ? "Retrying failed queries..." : "Some data failed to load"}
              </div>
              <div style={{ fontSize: "12px", marginTop: "4px", opacity: 0.8 }}>
                {[...relevantFailedQueries].join(", ")}
              </div>
            </>
          ) : (
            "Loading data..."
          )}
        </div>
        
        {/* Add keyframe animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};
